'use client'
import { GET } from '@/api/get-request';
import { POST } from '@/api/post-requests';
import { useDebounce } from '@/hooks/useDebounce';
import { country, department, FundingStatus, Match, Professor, RequestFilter, university, UsageStats } from '@/types/funding';
import { useNotifications } from '@toolpad/core/useNotifications';
import { useFormik } from 'formik';
import { usePathname, useRouter } from 'next/navigation';
import { createContext, useContext } from 'react';
import React from 'react';
import * as Yup from "yup";
import {DELETE} from "@/api/delete-request";
import {Rfs__funding} from "../../_refs/Rfs__funding";

const EMPTY_PROFESSOR_TEMPLATE: Professor = {
    id: 0,
    full_name: "[Data Missing!]",
    first_name: "",
    middle_name: "",
    last_name: "",
    occupation: "",
    department: "",
    email_address: "",
    url: "",
    research_areas: "",
    tags: [],
    funding_institute_id: 0,
    institution: {
        id: 0,
        institution_name: "",
        department_name: "",
        institution_url: "",
        logo_url: "",
        city: "",
        province: ""
    },
    already_selected_match_status: null
};

const patchMissingProfessorData = (requests: Match[]): Match[] => {
    return requests.map(request => {
        if (request.professor === null) {
            return {
                ...request,
                professor: EMPTY_PROFESSOR_TEMPLATE
            };
        }
        return request;
    });
};

type FormValues = {
    Country: string;
    ProfessorName: string;
    University: string;
    Department: string[];
    Search: { name: string, value: string }[];
    Page: number;
    More: boolean;
    professors: {
        id: number,
        selectedOption: string,
        isChecked: boolean
    }[];
    Requests_university: string,
    Requests_department: string,
    Requests_tags: string[],
    Requests_search: string,
    total: number

};
interface FundingTourState {
    /** Whether the tour should run */
    run: boolean;
    /** Current step index (for multi-page support) */
    stepIndex: number;
    /** Whether user has completed the tour */
    completed: boolean;
    /** Whether user has seen the tour before */
    hasSeenTour: boolean;
    /** Unique key to force re-mount on tour restart */
    tourKey: number;
}

interface FundingContextType {
    universities: university[],
    GetUniversities: (options?: { skipQueue?: boolean }) => void,
    countries: country[],
    GetCountries: (options?: { skipQueue?: boolean }) => void,
    formik: ReturnType<typeof useFormik<FormValues>>,
    departments: department[],
    professors: Professor[],
    GetProfessor: (force?: boolean, page?: number, options?: { skipQueue?: boolean }) => void,
    GetRequests: (page?: number, options?: { skipQueue?: boolean }) => void,
    AddProfessor: (id: number, match: string) => void,
    RemoveProfessor: (id: number) => void,
    loading: boolean,
    GetFilter: (options?: { skipQueue?: boolean }) => void,
    request_filter: RequestFilter,
    requests: Match[],
    handleUpdateRequest: (req: Match) => void,
    handleDeleteRequest: (req: Match) => void,
    FetchSuggestions: (query: string) => Promise<{ name: string, value: string }[]>,
    search_options: { name: string, value: string }[],
    fundingStatus: FundingStatus | null,
    GetFundingStatus: () => Promise<void>,
    statusLoading: boolean,
    usageStats: UsageStats | null,
    GetUsageStats: () => Promise<void>,
    usageLoading: boolean,
    showUpgradeModal: boolean,
    setShowUpgradeModal: (value: boolean) => void,
    upgradeModalTitle: number,
    setUpgradeModalTitle: (title: number) => void,
    isStatusChecking: boolean,
    statusCheckError: string | null,
    isRetryingStatus: boolean,
    showLoadingGenerateModal: boolean,
    setShowLoadingGenerateModal: (value: boolean) => void,
    showMessageSendingModal: boolean,
    setShowMessageSendingModal: (value: boolean) => void,
    isMounted: React.MutableRefObject<boolean>,
    // Tour Management
    fundingTourState: FundingTourState,
    startFundingTour: () => void,
    completeFundingTour: () => void,
    skipFundingTour: () => void,
    resetFundingTour: () => void,
}

const FundingContext = createContext<FundingContextType | null>(null);

export const FundingProvider = ({ children }: { children: React.ReactNode }) => {
    const [universities, setUniversities] = React.useState<university[]>([]);
    const [departments, setDepartments] = React.useState<department[]>([]);
    const [countries, setCountries] = React.useState<country[]>([]);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [professors, setProfessors] = React.useState<Professor[]>([]);
    const [requests, setRequests] = React.useState<Match[]>([]);
    const [search_options, setSearchOptions] = React.useState<{ name: string, value: string }[]>([]);
    const [fundingStatus, setFundingStatus] = React.useState<FundingStatus | null>(null);
    const [statusLoading, setStatusLoading] = React.useState<boolean>(true);
    const [usageStats, setUsageStats] = React.useState<UsageStats | null>(null);
    const [usageLoading, setUsageLoading] = React.useState<boolean>(false);
    const [showUpgradeModal, setShowUpgradeModal] = React.useState<boolean>(false);
    const [upgradeModalTitle, setUpgradeModalTitle] = React.useState<number>(2); // Default title
    const [isStatusChecking, setIsStatusChecking] = React.useState<boolean>(false);
    const [statusCheckError, setStatusCheckError] = React.useState<string | null>(null);
    const [showLoadingGenerateModal, setShowLoadingGenerateModal] = React.useState<boolean>(false);
    const [showMessageSendingModal, setShowMessageSendingModal] = React.useState<boolean>(false);
    const [isRetryingStatus, setIsRetryingStatus] = React.useState<boolean>(false);
    const [pendingCountriesFetch, setPendingCountriesFetch] = React.useState<boolean>(false);
    const [pendingUniversitiesFetch, setPendingUniversitiesFetch] = React.useState<boolean>(false);
    const [pendingFilterFetch, setPendingFilterFetch] = React.useState<boolean>(false);
    const [pendingProfessorFetch, setPendingProfessorFetch] = React.useState<{ active: boolean; page?: number }>({ active: false, page: undefined });
    const [pendingRequestsFetch, setPendingRequestsFetch] = React.useState<{ active: boolean; page?: number }>({ active: false, page: undefined });

    // Tour Management State
    const [fundingTourState, setFundingTourState] = React.useState<FundingTourState>({
        run: false,
        stepIndex: 0,
        completed: false,
        hasSeenTour: false,
        tourKey: 0,
    });

    const pathname = usePathname();
    const [request_filter, setRequestFilter] = React.useState<RequestFilter>({
        universities: [],
        departments: [],
        tags: []
    });
    const notifications = useNotifications();
    const router = useRouter();
    const isMounted = React.useRef(false);

    const validationSchema = Yup.object({
        professors: Yup.array().of(
            Yup.object({
                id: Yup.number().required(),
                selectedOption: Yup.string().nullable(),
                isChecked: Yup.boolean(),
            }).test(
                "selectedOption-and-isChecked",
                "Select Both (Match & Check)",
                function (value) {
                    if (!value) return true; // skip if empty row

                    const { selectedOption, isChecked } = value;

                    const isValid =
                        (!selectedOption && !isChecked) ||
                        (Boolean(selectedOption) && isChecked);

                    return isValid;
                }
            )
        ),
    });


    // const validationSchema = Yup.object({
    //     professors: Yup.array().of(
    //         Yup.object({
    //             id: Yup.number().required(),
    //             selectedOption: Yup.string().nullable(),
    //             isChecked: Yup.boolean(),
    //         }).test(
    //             "both or none",
    //             function (value) {
    //                 if (!value) return true;

    //                 const { selectedOption, isChecked } = value;
    //                 const isValid =
    //                     (!selectedOption && !isChecked) ||
    //                     (Boolean(selectedOption) && isChecked);

    //                 return isValid;
    //             }
    //         )
    //     ),
    // });

    const formik = useFormik<FormValues>({
        enableReinitialize: true,
        validationSchema: validationSchema,
        initialValues: {
            Country: '',
            ProfessorName: '',
            University: '',
            Department: [],
            Search: [],
            Page: 1,
            More: true,
            professors: [],
            Requests_university: '',
            Requests_department: '',
            Requests_tags: [],
            Requests_search: '',
            total: 0
        },
        onSubmit: async () => {
            router.push('/funding/complete-list')
        },
    });

    const isSrched = React.useRef(false);
    const currUniversity = React.useRef(formik.values.University);
    const currDepartment = React.useRef(formik.values.Department);
    const currCountry = React.useRef(formik.values.Country);
    const currProfessorName = React.useRef(formik.values.ProfessorName || '');
    const isResettingDepartment = React.useRef(false);
    const isResettingForCountry = React.useRef(false);

    async function GetRequests(page?: number, options?: { skipQueue?: boolean }) {
        const skipQueue = options?.skipQueue ?? false;

        // Block API call if status is being checked
        if (isStatusChecking) {
            console.log('GetRequests blocked: Status check in progress');
            if (!skipQueue) {
                setPendingRequestsFetch({ active: true, page });
            }
            return;
        }

        // Block API call if status is loading OR user is in onboarding state
        if (statusLoading || fundingStatus?.need_onboarding) {
            console.log('GetRequests blocked: Status loading or user in onboarding state');
            if (!skipQueue) {
                setPendingRequestsFetch({ active: true, page });
            }
            return;
        }

        if (page === 1)
            setLoading(true);
        // Status query logic:
        // - complete-list: complete + incomplete requests
        // - preview: preview status requests
        // - followup (default): reviewing + sent + answered requests
        const statusQuery = pathname?.includes('complete-list')
            ? 'complete&status%5B%5D=incomplete'
            : pathname?.includes('preview')
                ? 'preview'
                : 'reviewing&status%5B%5D=sent&status%5B%5D=answered&status%5B%5D=failed&status%5B%5D=sent&status%5B%5D=sending';

        const response = await GET({
            url: `v1/funding/requests?university_name=${encodeURIComponent(formik.values.Requests_university || '')}&department_id=${formik.values.Requests_department}&professor_name=${formik.values.Requests_search}&tags${formik.values.Requests_tags.length > 0 && '%5B%5D'}=${formik.values.Requests_tags}&page=${page || formik.values.Page}&per_page=20&status%5B%5D=${statusQuery}`
        });
        if (response.success) {
            const patchedData = patchMissingProfessorData(response.data.items);
            formik.setFieldValue('More', response.data.page < response.data.total);
            formik.setFieldValue('Page', response.data.page + 1);
            if (response.data.page === 1) {
                setRequests(patchedData);
                formik.setFieldValue('total', response.data.total);
            } else
                setRequests(prev => { return [...prev, ...patchedData] });
        } else
            notifications.show(response.message, {
                severity: 'error',
                autoHideDuration: 3000,
            });
        setLoading(false);
        setPendingRequestsFetch({ active: false, page: undefined });
    };

    const debouncedSearch = useDebounce<string>(formik.values.Requests_search, 500);

    React.useEffect(() => {
        GetRequests(1);
    }, [debouncedSearch]);

    function handleUpdateRequest(req: Match) {
        setRequests(prev =>
            prev.map(match =>
                match.id === req.id ? { ...match, ...req } : match
            )
        );
    };

    function handleDeleteRequest(req: Match) {
        setRequests(prev => prev.filter(match => match.id !== req.id));
    };


    async function GetCountries(options?: { skipQueue?: boolean }) {
        const skipQueue = options?.skipQueue ?? false;

        // Block API call if status is loading OR user is in onboarding state
        if (statusLoading || fundingStatus?.need_onboarding || isStatusChecking) {
            console.log('GetCountries blocked: Status loading or user in onboarding state');
            if (!skipQueue) {
                setPendingCountriesFetch(true);
            }
            return;
        }

        // Clear pending flag when executing
        setPendingCountriesFetch(false);

        const response = await GET({ url: 'v1/funding/countries' });
        if (response.success) {
            setCountries(response.data.items);
        }
    };

    async function GetUniversities(options?: { skipQueue?: boolean }) {
        const skipQueue = options?.skipQueue ?? false;

        // Block API call if status is loading OR user is in onboarding state
        if (statusLoading || fundingStatus?.need_onboarding || isStatusChecking) {
            console.log('GetUniversities blocked: Status loading or user in onboarding state');
            if (!skipQueue) {
                setPendingUniversitiesFetch(true);
            }
            return;
        }

        // Build URL with optional country parameter
        const countryParam = formik.values.Country
            ? `?country=${encodeURIComponent(formik.values.Country)}`
            : '';

        const response = await GET({ url: `v1/funding/universities${countryParam}` });
        if (response.success) {
            setUniversities(response.data.items);
        }
        setPendingUniversitiesFetch(false);
    };

    async function GetDepartment() {
        // Block API call if status is loading OR user is in onboarding state
        if (statusLoading || fundingStatus?.need_onboarding) {
            console.log('GetDepartment blocked: Status loading or user in onboarding state');
            return;
        }

        const response = await GET({ url: `v1/funding/universities/${formik.values.University}/departments` });
        if (response.success)
            setDepartments(response.data.items);
    }

    async function GetProfessor(force = false, page?: number, options?: { skipQueue?: boolean, justCountry?: boolean }) {
        const skipQueue = options?.skipQueue ?? false;
        // Block API call if status is being checked
        if (isStatusChecking && !force) {
            console.log('GetProfessor blocked: Status check in progress');
            if (!skipQueue) {
                setPendingProfessorFetch({ active: true, page });
            }
            return;
        }

        // Block API call if status is loading OR user is in onboarding state
        if ((statusLoading || fundingStatus?.need_onboarding) && !force) {
            console.log('GetProfessor blocked: Status loading or user in onboarding state');
            if (!skipQueue) {
                setPendingProfessorFetch({ active: true, page });
            }
            return;
        }

        setLoading(true);
        console.log(
            '___Going____Req__GetProfessor',
            'v1/funding/professors/search'
        );

        const requestBody = {
            tags: formik.values.Search.map(val => val?.name),
            page_number: page || formik.values.Page,
            page_size: 20
        };

        if (!options?.justCountry) {
            requestBody['institute_name'] = formik.values.University;
            requestBody['department_ids'] = formik.values.Department;
        }

        // Add optional parameters if they exist
        // ProfessorName: minimum 4 characters required
        if (formik.values.ProfessorName && formik.values.ProfessorName.length >= 4) {
            requestBody.professor_name = formik.values.ProfessorName;
        }
        if (formik.values.Country && formik.values.Country.length > 0) {
            requestBody.country = formik.values.Country;
        }

        const response = await POST({
            url: 'v1/funding/professors/search',
            body: requestBody
        });
        if (response.success) {
            formik.setFieldValue('More', response.data.has_more);
            formik.setFieldValue('Page', response.data.page_number + 1);
            if (response.data.page_number === 1)
                setProfessors(response.data.items);
            else
                setProfessors(prev => { return [...prev, ...response.data.items] });
        } else
            notifications.show(response.message, {
                severity: 'error',
                autoHideDuration: 3000,
            });
        setLoading(false);
        setPendingProfessorFetch({ active: false, page: undefined });

    };

    async function AddProfessor(id: number, match: string) {
        // Block API call if status is being checked
        if (isStatusChecking) {
            console.log('AddProfessor blocked: Status check in progress');
            notifications.show('Please wait while status is being checked...', {
                severity: 'info',
                autoHideDuration: 4000,
            });
            return;
        }

        // Block API call if status is loading OR user is in onboarding state
        if (statusLoading || fundingStatus?.need_onboarding) {
            console.log('AddProfessor blocked: Status loading or user in onboarding state');
            return;
        }

        const response = await POST({
            url: 'v1/funding/requests/add-professor',
            body: {
                "professor_id": id,
                "match_status": match
            }
        });
        notifications.show(response.message, {
            severity: response.success ? 'success' : 'error',
            autoHideDuration: 3000,
        });
    };

    async function RemoveProfessor(id: number) {
        // Block API call if status is being checked
        if (isStatusChecking) {
            console.log('RemoveProfessor blocked: Status check in progress');
            notifications.show('Please wait while status is being checked...', {
                severity: 'info',
                autoHideDuration: 2000,
            });
            return;
        }

        // Block API call if status is loading OR user is in onboarding state
        if (statusLoading || fundingStatus?.need_onboarding) {
            console.log('RemoveProfessor blocked: Status loading or user in onboarding state');
            return;
        }

        const response = await DELETE({
            url: `v1/funding/requests/${id}/fast`,
        });
        notifications.show(response.message, {
            severity: response.success ? 'success' : 'error',
            autoHideDuration: 3000,
        });
    };

    async function GetFilter(options?: { skipQueue?: boolean }) {
        const skipQueue = options?.skipQueue ?? false;

        // Block API call if status is loading OR user is in onboarding state OR status checking
        if (statusLoading || fundingStatus?.need_onboarding || isStatusChecking) {
            console.log('GetFilter blocked: Status loading or user in onboarding state');
            if (!skipQueue) {
                setPendingFilterFetch(true);
            }
            return;
        }

        // Clear pending flag when executing
        setPendingFilterFetch(false);

        const response = await GET({ url: 'v1/funding/requests/filters' });
        if (response.success)
            setRequestFilter(response.data)
    };

    // Helper function to extract usage stats from funding status
    function extractUsageStatsFromFundingStatus(status: FundingStatus | null): UsageStats | null {
        if (!status?.package?.features) return null;

        // انتخاب feature بر اساس trial status
        const featureKey = status.is_trial ? 'email_preview' : 'send_emails';
        const feature = status.package.features[featureKey];

        // Fallback اگر feature اصلی وجود نداشت
        if (!feature) {
            const fallbackFeature = status.package.features.send_emails || status.package.features.email_preview;
            if (!fallbackFeature) return null;
        }

        const targetFeature = feature || status.package.features.send_emails || status.package.features.email_preview;

        return {
            emails_sent: targetFeature.used,
            emails_limit: targetFeature.used + targetFeature.remaining, // کل limit
            plan_name: status.package.name,
            expires_at: status.package.expires_at
        };
    }

    // Retry mechanism ref to track retry attempts
    const statusCheckRetryRef = React.useRef<{ retryTimeout: NodeJS.Timeout | null }>({ retryTimeout: null });
    const isFetchingStatusRef = React.useRef<boolean>(false);

    async function GetFundingStatus(retryCount: number = 0) {
        if (isFetchingStatusRef.current) {
            console.log('GetFundingStatus skipped: fetch already in progress', { retryCount });
            return;
        }

        isFetchingStatusRef.current = true;
        setIsStatusChecking(true);
        setStatusLoading(true);
        setUsageLoading(true);
        setStatusCheckError(null); // Clear any previous errors

        try {
            const timeoutPromise = new Promise<never>((_, reject) => {
                setTimeout(() => {
                    reject(new Error('Funding status request timed out after 15 seconds'));
                }, 5000);
            });

            const response = await Promise.race([
                GET({ url: 'v1/funding/status' }),
                timeoutPromise
            ]);
            if (response.success) {
                setFundingStatus(response.data);

                // Extract and set usage stats from funding status
                const extractedUsageStats = extractUsageStatsFromFundingStatus(response.data);
                if (extractedUsageStats) {
                    setUsageStats(extractedUsageStats);
                }

                // Check upgrade conditions and set modal title
                let title = 2; // Default
                if (response.data.need_upgrade_trial_finished) title = 0;
                else if (response.data.need_upgrade_package_finished) title = 1;
                else if (response.data.need_upgrade_date_finished) title = 2;

                setUpgradeModalTitle(title);

                const needsUpgrade =
                    response.data.need_upgrade_trial_finished ||
                    response.data.need_upgrade_package_finished ||
                    response.data.need_upgrade_date_finished;

                if (needsUpgrade) {
                    setShowUpgradeModal(true);
                } else {
                    setShowUpgradeModal(false);
                }

                // Clear any pending retry timeout
                if (statusCheckRetryRef.current.retryTimeout) {
                    clearTimeout(statusCheckRetryRef.current.retryTimeout);
                    statusCheckRetryRef.current.retryTimeout = null;
                }

                setIsStatusChecking(false);
                setStatusLoading(false);
                setUsageLoading(false);
                setIsRetryingStatus(false);
            } else {
                // API returned error response
                throw new Error(response.message || 'Failed to fetch funding status');
            }
        } catch (error) {
            console.error('Failed to fetch funding status', error);

            // Retry logic: only retry once, after 20 seconds
            if (retryCount < 1) {
                setIsRetryingStatus(true);
                notifications.show('Retrying funding status check in 1 seconds...', {
                    severity: 'warning',
                    autoHideDuration: 3000,
                });

                // Schedule retry after 20 seconds (20000ms)
                statusCheckRetryRef.current.retryTimeout = setTimeout(() => {
                    setIsRetryingStatus(false);
                    GetFundingStatus(retryCount + 1);
                }, 1000);
            } else {
                // Retry failed - show error and stop process
                const errorMessage = error instanceof Error
                    ? error.message
                    : 'Failed to check funding status. Please refresh the page or contact support.';

                setStatusCheckError(errorMessage);
                setShowUpgradeModal(false);
                setIsStatusChecking(false);
                setStatusLoading(false);
                setUsageLoading(false);
                setIsRetryingStatus(false);

                notifications.show(errorMessage, {
                    severity: 'error',
                    autoHideDuration: 5000,
                });
            }
        } finally {
            isFetchingStatusRef.current = false;
        }
    };

    // Wrapper function that extracts usage stats from fundingStatus
    // Falls back to mock data if fundingStatus is not available
    async function GetUsageStats() {
        setUsageLoading(true);
        try {
            // Try to extract from existing fundingStatus
            const extractedStats = extractUsageStatsFromFundingStatus(fundingStatus);
            if (extractedStats) {
                setUsageStats(extractedStats);
            } else {
                // Fallback to mock data if fundingStatus is not available
                // This maintains backward compatibility
                await new Promise(resolve => setTimeout(resolve, 600));
                const mockData: UsageStats = {
                    emails_sent: 30,
                    emails_limit: 100,
                    plan_name: "Free Trial",
                    expires_at: "2025-12-01T00:00:00Z"
                };
                setUsageStats(mockData);
            }
        } catch (error) {
            console.error('Failed to fetch usage stats', error);
            notifications.show('Failed to fetch usage statistics', {
                severity: 'error',
                autoHideDuration: 3000,
            });
        } finally {
            setUsageLoading(false);
        }
    };

    const FetchSuggestions = async (query: string): Promise<{ name: string, value: string }[]> => {
        // Block API call if status is loading OR user is in onboarding state
        if (statusLoading || fundingStatus?.need_onboarding) {
            console.log('FetchSuggestions blocked: Status loading or user in onboarding state');
            return [];
        }

        const response = await GET({ url: `v1/funding/tag-suggestions?q=${query}` });
        if (response.status) {
            setSearchOptions(response.suggestions.map((val: { tag: string }) => ({ name: val.tag, value: val.tag })))
            return response.suggestions.map((val: { tag: string }) => ({ name: val.tag, value: val.tag }))
        } else return []

    };

    React.useEffect(() => {
        if (professors.length > 0) {
            const existingProfessors = formik.values.professors;

            const updatedProfessors = professors.map((p) => {
                // If this professor already exists in formik, preserve their state
                const existing = existingProfessors.find(ep => ep.id === p.id);

                if (existing) {
                    return existing; // Preserve user's previous selections
                } else {
                    // New professor: initialize based on API data
                    return {
                        id: p.id,
                        selectedOption: p.already_selected_match_status !== null
                            ? String(p.already_selected_match_status)
                            : "",
                        isChecked: p.already_selected_match_status !== null,
                    };
                }
            });

            formik.setFieldValue("professors", updatedProfessors);
        }
    }, [professors]);


    React.useEffect(() => {
        formik.setFieldValue('Requests_department', '');
    }, [formik.values.Requests_university]);


    Rfs__funding.useEffect_resetCountry();
    // Handle Country change: reset University and Department, reload universities
    React.useEffect(() => {
        // Mark that we're resetting University/Department due to Country change
        isResettingForCountry.current = true;

        // Reset University and Department when Country changes
        formik.setFieldValue('University', '');
        formik.setFieldValue('Department', []);

        // Reload universities with country filter (or all if country is empty)
        GetUniversities();

        setTimeout(() => {
            isResettingForCountry.current = false; // Reset flag
        }, 1000)

        // Don't call GetProfessor here - let the main useEffect handle it to avoid duplicate calls
    }, [formik.values.Country]);


    Rfs__funding.useEffect_GetProfessor();
    // ProfessorName is now debounced in the component itself (ProfessorNameSearch)
    // It only updates formik when >= 4 characters, so we can use it directly
    React.useEffect(() => {
        // Skip if we're resetting University/Department due to Country change
        if (isResettingForCountry.current) {
            // Handle Country change
            const isChngingCountry = currCountry.current !== formik.values.Country;
            if (isChngingCountry) {
                currCountry.current = formik.values.Country;
                // Call GetProfessor even if country is empty (for reset case)
                GetProfessor(false, 1, { justCountry: true });
            }
            return; // Skip other logic to avoid duplicate calls
        }

        // Handle Country change separately to avoid duplicate calls
        const isChngingCountry = currCountry.current !== formik.values.Country;
        if (isChngingCountry) {
            currCountry.current = formik.values.Country;
            // Call GetProfessor even if country is empty (for reset case)
            GetProfessor(false, 1);
            return; // Skip other logic to avoid duplicate calls
        }

        // Check if ProfessorName is changing
        const isChngingProfessorName = currProfessorName.current !== (formik.values.ProfessorName || '');
        // Check if user is clearing ProfessorName (had >= 4 chars, now empty)
        const wasNotEmpty = currProfessorName.current && currProfessorName.current.length >= 4;
        const isNowEmpty = !formik.values.ProfessorName || formik.values.ProfessorName.length === 0;
        const isClearing = isChngingProfessorName && wasNotEmpty && isNowEmpty;
        // Check if new value is valid (>= 4 chars)
        const isValidNewValue = formik.values.ProfessorName && formik.values.ProfessorName.length >= 4;

        // Check if University is being reset (from value to empty)
        const wasUniversityNotEmpty = currUniversity.current && currUniversity.current.length > 0;
        const isNowUniversityEmpty = !formik.values.University || formik.values.University.length === 0;
        const isResettingUniversity = wasUniversityNotEmpty && isNowUniversityEmpty;

        const isNotNewSrch = (formik.values.Search.length === 0 && !isSrched.current);
        const isNotDepartment = formik.values.Department.length === 0;
        const isNotCountry = formik.values.Country.length === 0;
        const isNotProfessorName = !formik.values.ProfessorName || formik.values.ProfessorName.length === 0;
        const isChgedDepartment = currDepartment.current.length !== formik.values.Department.length;
        const isUniversity = !!formik.values.University;

        // Skip logic: if clearing ProfessorName or valid new value, don't skip
        // Otherwise, apply normal skip logic
        const shouldSkipProfessorName = !isClearing && !(isChngingProfessorName && isValidNewValue);
        // Don't skip if University is being reset
        if (!isUniversity && isNotDepartment && isNotNewSrch && isNotCountry && (isNotProfessorName && shouldSkipProfessorName) && !isChgedDepartment && !isResettingUniversity) {
            return;
        }
        const justUniversity = isUniversity && isNotDepartment && isNotNewSrch && !isChgedDepartment;
        if (justUniversity) {
            if (currUniversity.current === formik.values.University) return;
        }

        let goingResettingDepartment = false;
        const isChngingUniversity = currUniversity.current !== formik.values.University;
        if (isChngingUniversity) {
            if (formik.values.Department.length) {
                goingResettingDepartment = true;
            }
            if (formik.values.University.length > 0) {
                GetDepartment();
            }
        }
        isSrched.current = formik.values.Search.length !== 0;
        currUniversity.current = formik.values.University;
        if (isChgedDepartment) {
            currDepartment.current = formik.values.Department;
        }
        // Update currProfessorName if it changed
        if (isChngingProfessorName) {
            currProfessorName.current = formik.values.ProfessorName || '';
        }
        if (isResettingDepartment.current) {
            isResettingDepartment.current = false;
            return;
        }

        console.log(
            '___Going____GetProfessor__1__[isNotNewSrch, isNotDepartment, isChgedDepartment, isUniversity, justUniversity, isChngingUniversity, goingResettingDepartment]',
            [isNotNewSrch, isNotDepartment, isChgedDepartment, isUniversity, justUniversity, isChngingUniversity, goingResettingDepartment]
        );
        GetProfessor(false, 1);
        if (goingResettingDepartment) {
            isResettingDepartment.current = true;
            formik.setFieldValue('Department', []);
        }
    }, [
        formik.values.Department,
        formik.values.Search,
        formik.values.University,
        formik.values.Country,
        formik.values.ProfessorName
    ]);


    React.useEffect(() => {
        GetRequests(1);
    }, [
        formik.values.Requests_department,
        formik.values.Requests_university,
        formik.values.Requests_tags
    ]);

    React.useEffect(() => {
        const readyForFetch = !statusLoading && !isStatusChecking;
        const onboardingBlocked = Boolean(fundingStatus?.need_onboarding);

        if (readyForFetch && !onboardingBlocked) {
            if (pendingCountriesFetch) {
                GetCountries({ skipQueue: true });
            }

            if (pendingUniversitiesFetch) {
                GetUniversities({ skipQueue: true });
            }

            if (pendingFilterFetch) {
                GetFilter({ skipQueue: true });
            }

            if (pendingProfessorFetch.active) {
                if (isMounted.current) {
                    isMounted.current = false;
                    return;
                }
                const queuedPage = pendingProfessorFetch.page;
                console.log(
                    '___Going____GetProfessor__2__[statusLoading, isStatusChecking, fundingStatus?.need_onboarding, pendingUniversitiesFetch, pendingProfessorFetch]',
                    [statusLoading, isStatusChecking, fundingStatus?.need_onboarding, pendingUniversitiesFetch, pendingProfessorFetch]
                );
                GetProfessor(false, queuedPage, { skipQueue: true });
            }

            if (pendingRequestsFetch.active) {
                const queuedPage = pendingRequestsFetch.page;
                console.log(
                    '___Going____GetRequests__Replay__[statusLoading, isStatusChecking, fundingStatus?.need_onboarding, pendingRequestsFetch]',
                    [statusLoading, isStatusChecking, fundingStatus?.need_onboarding, pendingRequestsFetch]
                );
                GetRequests(queuedPage, { skipQueue: true });
            }
        }
    }, [statusLoading, isStatusChecking, fundingStatus?.need_onboarding, pendingCountriesFetch, pendingUniversitiesFetch, pendingFilterFetch, pendingProfessorFetch, pendingRequestsFetch]);

    // Cleanup retry timeout on unmount
    React.useEffect(() => {
        return () => {
            if (statusCheckRetryRef.current.retryTimeout) {
                clearTimeout(statusCheckRetryRef.current.retryTimeout);
            }
        };
    }, []);

    // Tour Management: Check if user should see tour on mount
    // Checks localStorage for 'fundingTourCompleted' flag and auto-starts on first visit
    // Only shows tour if user is NOT in onboarding state (has package or is trial)
    React.useEffect(() => {
        // Only check on client-side
        if (typeof window === 'undefined') return;

        // Wait for funding status to be loaded before deciding on tour
        if (statusLoading || fundingStatus === null) return;

        // Don't show tour if user is in onboarding state (hasn't purchased yet)
        if (fundingStatus?.need_onboarding) return;

        const tourCompleted = localStorage.getItem('fundingTourCompleted');
        const hasSeenTour = localStorage.getItem('fundingTourSeen');

        // Auto-start tour on first visit if not completed and not seen before
        if (!tourCompleted && !hasSeenTour && pathname === '/funding/templates') {
            setFundingTourState(prev => ({
                ...prev,
                run: true,
                hasSeenTour: true,
            }));
            localStorage.setItem('fundingTourSeen', 'true');
            console.log('[FundingTour] Auto-starting tour on first visit');
        }
    }, [pathname, statusLoading, fundingStatus]);

    /**
     * Start the funding tour manually
     */
    const startFundingTour = React.useCallback(() => {
        setFundingTourState(prev => ({
            run: true,
            stepIndex: 0,
            completed: false,
            hasSeenTour: true,
            tourKey: prev.tourKey + 1, // Increment to force re-mount
        }));
        localStorage.setItem('fundingTourSeen', 'true');
        console.log('[FundingTour] Tour started manually');
    }, []);

    /**
     * Complete the funding tour
     * Saves completion status to localStorage
     */
    const completeFundingTour = React.useCallback(() => {
        setFundingTourState(prev => ({
            ...prev,
            run: false,
            completed: true,
        }));
        localStorage.setItem('fundingTourCompleted', 'true');
        console.log('[FundingTour] Tour completed and saved to localStorage');
    }, []);

    /**
     * Skip the funding tour
     */
    const skipFundingTour = React.useCallback(() => {
        setFundingTourState(prev => ({
            ...prev,
            run: false,
        }));
        // Don't mark as completed so user can restart if needed
        console.log('[FundingTour] Tour skipped');
    }, []);

    /**
     * Reset tour (for testing or user-initiated restart)
     */
    const resetFundingTour = React.useCallback(() => {
        if (typeof window !== 'undefined') {
            localStorage.removeItem('fundingTourCompleted');
            localStorage.removeItem('fundingTourSeen');
        }
        setFundingTourState(prev => ({
            run: false,
            stepIndex: 0,
            completed: false,
            hasSeenTour: false,
            tourKey: prev.tourKey + 1, // Increment to force re-mount on next start
        }));
        console.log('[FundingTour] Tour reset');
    }, []);

    return (
        <FundingContext.Provider value={{
            universities, GetUniversities,
            countries, GetCountries,
            formik, departments, professors,
            GetProfessor, AddProfessor, RemoveProfessor, loading,
            GetFilter, request_filter, requests,
            GetRequests, handleUpdateRequest,
            handleDeleteRequest, FetchSuggestions, search_options,
            fundingStatus, GetFundingStatus, statusLoading,
            usageStats, GetUsageStats, usageLoading,
            showUpgradeModal, setShowUpgradeModal, upgradeModalTitle, setUpgradeModalTitle,
            isStatusChecking, statusCheckError, isRetryingStatus,
            showLoadingGenerateModal, setShowLoadingGenerateModal,
            showMessageSendingModal, setShowMessageSendingModal, isMounted,
            // Tour Management
            fundingTourState,
            startFundingTour,
            completeFundingTour,
            skipFundingTour,
            resetFundingTour,
        }}>
            {children}
        </FundingContext.Provider>
    );
};

export const useFunding = () => {
    const context = useContext(FundingContext);
    if (!context) {
        throw new Error('useFunding must be used within an FundingProvider');
    }
    return context;
};
